import { useMutation } from "@apollo/client";
import { Intent, OverlayToaster, Position } from "@blueprintjs/core";
import {
    segmentTrackAdvancedStudiesViewAdvancedStudiesMenuItemClicked,
    segmentTrackScreeningViewDelete,
    segmentTrackScreeningViewExport,
    segmentTrackSidebarMenuItemClicked
} from "analytics/analyticTrackEvents";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useScreeningViewContext } from "contexts/ScreeningViewContext/ScreeningViewContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    ArchiveScreeningViewMutation,
    ArchiveScreeningViewMutationVariables,
    GetRestudiedBusesQuery,
    useGetRestudiedBusesQuery
} from "generated/graphql";
import { loader } from "graphql.macro";
import {
    PlatformAction,
    useSetPlatformActions
} from "infrastructure/platform/PlatformShellContext";
import { getUrlForBusPage, PROSPECTING_PATH } from "infrastructure/RouterApp";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LOAD_TYPE } from "types/generatorType";
import { getRedirectUrl, getRegionToSpaceDict } from "types/gitBook";
import { ADMIN_TYPE } from "types/roleType";
import {
    canUseAdvancedStudies,
    isLocalStudiesEnabledForRegion
} from "types/scope";
import { maybeGetGenerationFuelType } from "types/scopeViewType";
import { ScreeningView } from "types/screeningViewType";
import {
    getLoadOnlyFlag,
    getLocalStudiesFlag
} from "types/teamFeatureFlagType";
import ViewAdvancedStudiesDialog from "./advancedStudies/ViewAdvancedStudiesDialog";
import ChangeRegionDialog from "./regionSelector/ChangeRegionDialog";
import {
    ExportFilterType,
    ExportType,
    EXPORT_FILTER_ALL,
    EXPORT_FILTER_HIGH_CAPACITY,
    KMZ_TYPE,
    SHAPEFILE_TYPE,
    useExportScreeningView
} from "./sidebar/exports/exportHelpers";
import AboutScreeningViewDialog from "./sidebar/screeningView/AboutScreeningViewDialog";
import CreateScreeningViewDialog from "./sidebar/screeningView/CreateScreeningViewDialog";

const ARCHIVE_SCREENING_VIEW = loader(
    "src/graphql/archiveScreeningView.graphql"
);
const ScreeningViewToaster = OverlayToaster.create({
    position: Position.TOP
});

type RestudiedBusEntry = GetRestudiedBusesQuery["buses_to_scopes"][number];

type RestudiedBus = {
    readonly niraBusId: string;
    readonly busDisplayName: string;
    readonly region: string;
    readonly scope: string;
    readonly scopeDisplayName: string;
};

const groupRestudiedBuses = (
    entries: ReadonlyArray<RestudiedBusEntry>
): ReadonlyArray<RestudiedBus> => {
    const byBus = new Map<string, RestudiedBus>();
    const latestCreatedAt = new Map<string, string>();
    for (const entry of entries) {
        const createdAt = entry.scope_info?.user_scope_config?.created_at ?? "";
        const previousCreatedAt = latestCreatedAt.get(entry.nira_bus_id);
        if (previousCreatedAt === undefined || createdAt > previousCreatedAt) {
            latestCreatedAt.set(entry.nira_bus_id, createdAt);
            byBus.set(entry.nira_bus_id, {
                niraBusId: entry.nira_bus_id,
                busDisplayName:
                    entry.bus?.bus_display_name ?? entry.nira_bus_id,
                region: entry.bus?.region ?? "",
                scope: entry.scope,
                scopeDisplayName: entry.scope_info?.display_name ?? entry.scope
            });
        }
    }
    return Array.from(byBus.values());
};

enum DialogType {
    NEW_SCREENING_VIEW = "NEW_SCREENING_VIEW",
    ABOUT_SCREENING_VIEW = "ABOUT_SCREENING_VIEW",
    CHANGE_REGION = "CHANGE_REGION",
    VIEW_ADVANCED_STUDIES = "VIEW_ADVANCED_STUDIES",
    NONE = "NONE"
}

const ProspectingNavActions: React.FC = () => {
    const [archiveScreeningView] = useMutation<
        ArchiveScreeningViewMutation,
        ArchiveScreeningViewMutationVariables
    >(ARCHIVE_SCREENING_VIEW);
    const exportScreeningViewToKmz = useExportScreeningView(KMZ_TYPE);
    const exportScreeningViewToShapefile =
        useExportScreeningView(SHAPEFILE_TYPE);

    const navigate = useNavigate();
    const { region, generator } = useMapViewRoutingMetadata();
    const maybeScreeningViewData = useScreeningViewContext();
    const user = useUserDataContext();
    // Load-only teams cannot open the bus page, so hide its links too.
    const showRestudiedBuses =
        generator !== LOAD_TYPE &&
        user.teamFeatureFlags.includes(getLocalStudiesFlag(region)) &&
        !user.teamFeatureFlags.includes(getLoadOnlyFlag(region)) &&
        isLocalStudiesEnabledForRegion(region);
    const {
        busFiltersConfiguration: {
            busFilters: { scope }
        }
    } = useMapViewConfiguration();
    const showAdvancedStudies = canUseAdvancedStudies(user, region, scope);

    const setActions = useSetPlatformActions();

    const { data: restudiedBusesData } = useGetRestudiedBusesQuery({
        variables: { region },
        skip: !showRestudiedBuses || showAdvancedStudies
    });
    const restudiedBuses = useMemo(
        () =>
            restudiedBusesData
                ? groupRestudiedBuses(restudiedBusesData.buses_to_scopes)
                : [],
        [restudiedBusesData]
    );

    const [currentDialog, setCurrentDialog] = useState<DialogType>(
        DialogType.NONE
    );
    const closeDialog = () => setCurrentDialog(DialogType.NONE);

    const maybeRegionMethodologySpace = getRegionToSpaceDict()[region];
    // This will be undefined if the region is not supported yet or if the user
    // doesn't have access to the region (which should never happen if they get to this page).
    const maybeRegionMethodologyLink =
        maybeRegionMethodologySpace === undefined
            ? undefined
            : getRedirectUrl(maybeRegionMethodologySpace, null, user);

    const onClickExport = async ({
        screeningViewData,
        exportType,
        busFilterType
    }: {
        screeningViewData: ScreeningView;
        exportType: ExportType;
        busFilterType: ExportFilterType;
    }) => {
        ScreeningViewToaster.show({
            message: "Export in progress.",
            intent: Intent.PRIMARY
        });

        const exportTypeText =
            exportType === SHAPEFILE_TYPE ? "Shapefile" : "KMZ";
        try {
            const exportFunc =
                exportType === SHAPEFILE_TYPE
                    ? exportScreeningViewToShapefile
                    : exportScreeningViewToKmz;
            await exportFunc(busFilterType);
        } catch (error) {
            ScreeningViewToaster.show({
                message: `Encountered an error while exporting ${exportTypeText}.`,
                intent: Intent.DANGER
            });
            return;
        }

        ScreeningViewToaster.show({
            message: `Exported as ${exportTypeText}.`,
            intent: Intent.SUCCESS
        });
        segmentTrackScreeningViewExport(screeningViewData.id, exportType);
    };

    const actions: PlatformAction[] = [];

    if (showAdvancedStudies) {
        actions.push({
            icon: "clean",
            label: "Advanced studies",
            onClick: () => {
                segmentTrackAdvancedStudiesViewAdvancedStudiesMenuItemClicked({
                    source: "nav_actions"
                });
                setCurrentDialog(DialogType.VIEW_ADVANCED_STUDIES);
            }
        });
    } else if (showRestudiedBuses) {
        const restudyChildren: ReadonlyArray<PlatformAction> =
            restudiedBuses.length > 0
                ? restudiedBuses.map((bus) => ({
                      icon: "map-marker" as const,
                      label: bus.busDisplayName,
                      onClick: () =>
                          window.open(
                              getUrlForBusPage(
                                  bus.niraBusId,
                                  maybeGetGenerationFuelType(region, generator)
                              ),
                              "_blank"
                          )
                  }))
                : [
                      {
                          icon: "search" as const,
                          label: "No re-studies yet",
                          disabled: true
                      }
                  ];
        actions.push({
            icon: "clean",
            label: "Re-studied buses",
            children: restudyChildren
        });
    }

    actions.push({
        icon: "globe",
        label: "Change region",
        onClick: () => {
            setCurrentDialog(DialogType.CHANGE_REGION);
        }
    });

    if (maybeScreeningViewData !== undefined) {
        const screeningViewChildren: PlatformAction[] = [
            {
                icon: "info-sign",
                label: "About",
                onClick: () => {
                    setCurrentDialog(DialogType.ABOUT_SCREENING_VIEW);
                    segmentTrackSidebarMenuItemClicked("ABOUT_SCREENING_VIEW");
                }
            },
            {
                icon: "export",
                label: "Export",
                children: [
                    {
                        icon: "area-of-interest",
                        label: "KMZ (all)",
                        onClick: () =>
                            onClickExport({
                                screeningViewData: maybeScreeningViewData,
                                exportType: KMZ_TYPE,
                                busFilterType: EXPORT_FILTER_ALL
                            })
                    },
                    {
                        icon: "area-of-interest",
                        label: "KMZ (green)",
                        onClick: () =>
                            onClickExport({
                                screeningViewData: maybeScreeningViewData,
                                exportType: KMZ_TYPE,
                                busFilterType: EXPORT_FILTER_HIGH_CAPACITY
                            })
                    },
                    {
                        icon: "shapes",
                        label: "Shapefile (all)",
                        onClick: () =>
                            onClickExport({
                                screeningViewData: maybeScreeningViewData,
                                exportType: SHAPEFILE_TYPE,
                                busFilterType: EXPORT_FILTER_ALL
                            })
                    },
                    {
                        icon: "shapes",
                        label: "Shapefile (green)",
                        onClick: () =>
                            onClickExport({
                                screeningViewData: maybeScreeningViewData,
                                exportType: SHAPEFILE_TYPE,
                                busFilterType: EXPORT_FILTER_HIGH_CAPACITY
                            })
                    }
                ]
            }
        ];

        if (user.role === ADMIN_TYPE) {
            screeningViewChildren.push({
                icon: "trash",
                label: "Delete",
                intent: Intent.DANGER,
                onClick: () => {
                    ScreeningViewToaster.show({
                        message:
                            "Are you sure you want to delete this screening view? " +
                            "You'll be taken back to the home page once deleted.",
                        intent: Intent.DANGER,
                        action: {
                            icon: "trash",
                            text: "Delete",
                            onClick: () => {
                                archiveScreeningView({
                                    variables: {
                                        id: maybeScreeningViewData.id
                                    },
                                    onCompleted: () => {
                                        segmentTrackScreeningViewDelete(
                                            maybeScreeningViewData.id
                                        );
                                        navigate(`/${PROSPECTING_PATH}`);
                                    }
                                });
                            }
                        },
                        timeout: undefined
                    });
                }
            });
        }

        actions.push({
            icon: "map",
            label: "Screening view",
            children: screeningViewChildren
        });
    }

    if (maybeScreeningViewData === undefined && user.role === ADMIN_TYPE) {
        actions.push({
            icon: "map-create",
            label: "New screening view",
            onClick: () => {
                setCurrentDialog(DialogType.NEW_SCREENING_VIEW);
                segmentTrackSidebarMenuItemClicked("NEW_SCREENING_VIEW");
            }
        });
    }

    actions.push({
        icon: "manual",
        label: "Documentation",
        disabled: maybeRegionMethodologyLink === undefined,
        onClick: () => {
            if (maybeRegionMethodologyLink !== undefined) {
                segmentTrackSidebarMenuItemClicked("DOCUMENTATION");
                window.open(maybeRegionMethodologyLink, "_blank");
            }
        }
    });

    useEffect(() => {
        setActions(actions);
        return () => setActions([]);
    }, [
        setActions,
        restudiedBuses,
        generator,
        showAdvancedStudies,
        showRestudiedBuses
    ]);

    return (
        <>
            <AboutScreeningViewDialog
                isOpen={currentDialog === DialogType.ABOUT_SCREENING_VIEW}
                closeDialog={closeDialog}
            />
            <CreateScreeningViewDialog
                isOpen={currentDialog === DialogType.NEW_SCREENING_VIEW}
                closeDialog={closeDialog}
            />
            <ChangeRegionDialog
                isOpen={currentDialog === DialogType.CHANGE_REGION}
                closeDialog={closeDialog}
                currentRegionAndGeneratorType={{
                    region,
                    generatorType: generator
                }}
                currentScreeningView={maybeScreeningViewData}
            />
            <ViewAdvancedStudiesDialog
                isOpen={currentDialog === DialogType.VIEW_ADVANCED_STUDIES}
                onClose={closeDialog}
            />
        </>
    );
};

export default ProspectingNavActions;
